import { ReactNode } from 'react';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { PDFViewer } from '@/components/common/pdf-viewer';
import { ExcelDocViewer } from '@/components/xlsx-viewer/ExcelDocViewer';
import { ImageWithSmartScaling } from '@/components/common/ImageWithSmartScaling';
import { isExcelFile } from './utils';
import { processEmailHtml } from './email-html';
import { T, useGT } from 'gt-react';
import type { EmailAttachment, DocumentAssociation } from '@erp/rpc';

/**
 * Builds a map of content IDs to signed URLs for inline email images
 * @param documentAssociations - Document associations that may contain inline images
 * @returns Map of content ID to signed URL
 */
export function buildCidMap(
  documentAssociations?: Array<{
    document: {
      contentId?: string | null;
      url: string;
    };
  }>,
): Map<string, string> {
  const cidMap = new Map<string, string>();

  if (!documentAssociations) return cidMap;

  for (const association of documentAssociations) {
    const { document } = association;
    if (document.contentId && document.url) {
      // Store without angle brackets if present (some email clients include them)
      let cleanContentId = document.contentId.replace(/^<|>$/g, '');
      // Strip @... suffix to match how the regex extracts the contentId
      // (e.g., "image001.png@01234567.ABCDEF" -> "image001.png")
      const atIndex = cleanContentId.indexOf('@');
      if (atIndex !== -1) {
        cleanContentId = cleanContentId.substring(0, atIndex);
      }
      cidMap.set(cleanContentId, document.url);
    }
  }

  return cidMap;
}

/**
 * Renders email body content with styling
 * @param content - HTML or text content to render
 * @param cidMap - Optional map of content IDs to signed URLs for inline images
 */
export function renderEmailBody(
  content?: string | null,
  cidMap?: Map<string, string>,
): ReactNode {
  if (!content) return null;

  const hasContent =
    content
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim().length > 0;

  const sanitizedContent = processEmailHtml({
    html: content,
    cidMap,
  });

  return hasContent ? (
    <div
      className="prose prose-sm max-w-none
            [&_img]:max-w-full
            [&_a]:text-primary [&_a]:underline
            [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-sm
            [&_h1]:mb-2 [&_h2]:mb-2 [&_h3]:mb-2 [&_h4]:mb-2 [&_h5]:mb-2 [&_h6]:mb-2
            [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:pl-6 [&_ol]:mb-4
            [&_blockquote]:border-l-4 [&_blockquote]:border-muted [&_blockquote]:pl-4 [&_blockquote]:ml-0 [&_blockquote]:mb-4 [&_blockquote]:italic
            [&_pre]:bg-muted [&_pre]:p-4 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:mb-4
            [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
            overflow-x-auto"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is properly sanitized with DOMPurify before rendering
      dangerouslySetInnerHTML={{
        __html: sanitizedContent,
      }}
    />
  ) : (
    <div className="flex items-center justify-center p-16 border border-dashed rounded-lg">
      <p className="text-sm text-muted-foreground italic opacity-50 tracking-wide">
        <T context="email viewer">No email body content</T>
      </p>
    </div>
  );
}

/**
 * Renders email attachments with proper viewers
 * @param attachments - Legacy email attachments
 * @param documentAssociations - Document associations (new system)
 * @param highlightedDocumentIds - Document IDs to highlight (for showing source documents)
 */
interface NormalizedEmailAttachment {
  id: string;
  documentId?: string;
  fileName: string;
  sizeBytes: number;
  mimeType?: string;
  s3Url: string;
}

function isPdf(fileName: string, mimeType?: string): boolean {
  return (
    mimeType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf')
  );
}

/**
 * Renders a single attachment card (file name, open-in-new-tab button, and
 * the appropriate inline viewer). Shared by the main attachment list and
 * the split-documents section.
 */
function renderAttachmentItem(
  attachment: NormalizedEmailAttachment,
  isHighlighted: boolean,
  onRefreshData?: () => void,
): ReactNode {
  return (
    <div
      key={attachment.id}
      className={`space-y-2 p-2 rounded-md ${
        isHighlighted ? 'border-2 border-primary bg-primary/5' : ''
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        {/* eslint-disable i18next/no-literal-string -- bullet unicode symbol and KB unit abbreviation, not translatable */}
        <h6 className="text-sm font-medium break-words">
          {isHighlighted && <span className="text-primary mr-1">●</span>}
          {attachment.fileName} ({(attachment.sizeBytes / 1024).toFixed(2)} KB)
        </h6>
        {/* eslint-enable i18next/no-literal-string */}
        <Button
          variant="link"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            window.open(attachment.s3Url, '_blank');
          }}
          className="h-auto p-1"
        >
          <span className="sr-only">
            <T context="action, open attachment in new tab">
              Open attachment in new tab
            </T>
          </span>
          <ArrowSquareOutIcon className="w-4 h-4" />
        </Button>
      </div>
      {attachment.mimeType?.startsWith('image/') ? (
        <ImageWithSmartScaling
          src={attachment.s3Url}
          alt={attachment.fileName}
          fileName={attachment.fileName}
          sizeBytes={attachment.sizeBytes}
        />
      ) : isPdf(attachment.fileName, attachment.mimeType) ? (
        <PDFViewer
          url={attachment.s3Url}
          pageMode="single"
          onUrlExpired={onRefreshData}
        />
      ) : isExcelFile({
          fileName: attachment.fileName,
          mimeType: attachment.mimeType,
        }) ? (
        <ExcelDocViewer
          url={attachment.s3Url}
          fileName={attachment.fileName}
          documentId={attachment.documentId}
        />
      ) : (
        <a
          href={attachment.s3Url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline text-sm hover:text-primary/80"
          onClick={(e) => e.stopPropagation()}
        >
          <T context="action, download a file">Download File</T>
        </a>
      )}
    </div>
  );
}

/**
 * Map document associations (oRPC thread shape) to the normalized
 * attachment item shape.
 */
export function documentAssociationsToAttachmentItems(
  documentAssociations?: DocumentAssociation[],
): NormalizedEmailAttachment[] {
  return (documentAssociations || []).map((doc) => ({
    id: doc.id,
    documentId: doc.document.id,
    fileName: doc.document.originalFileName || doc.document.name,
    sizeBytes: doc.document.fileSizeBytes || 0,
    mimeType: doc.document.mimeType || undefined,
    s3Url: doc.document.url,
  }));
}

/**
 * Map REST inbound-email attachments ({originalName, ...}) to the
 * normalized attachment item shape.
 */
export function restAttachmentsToAttachmentItems(
  attachments?: Array<{
    id: string;
    originalName: string;
    s3Url: string;
    mimeType: string;
    sizeBytes: number;
  }>,
): NormalizedEmailAttachment[] {
  return (attachments || []).map((att) => ({
    id: att.id,
    documentId: att.id, // REST attachments mapped from documents use the document id
    fileName: att.originalName,
    sizeBytes: att.sizeBytes,
    mimeType: att.mimeType,
    s3Url: att.s3Url,
  }));
}

/**
 * Toggle button + separated section for split-child documents (documents
 * created by splitting a parent attachment). Renders nothing when the email
 * has no split children. The caller owns the show state, since toggling it
 * must rerun the email/thread query with includeSplitDocuments.
 */
export function SplitDocumentsSection({
  count,
  attachments,
  show,
  onToggle,
  isLoading,
  onRefreshData,
}: {
  /** Number of split-child documents (from splitDocumentCount / splitAttachmentsCount). */
  count: number;
  /** Split-child attachments; undefined until fetched with the flag on. */
  attachments?: NormalizedEmailAttachment[];
  show: boolean;
  onToggle: () => void;
  isLoading?: boolean;
  onRefreshData?: () => void;
}) {
  const gt = useGT();

  if (count <= 0) return null;

  const buttonLabel = show
    ? gt('Hide split documents', {
        context: 'button label to hide split-child documents of an email',
      })
    : count === 1
      ? gt('Show {count} split document', {
          context:
            'button label to show split-child documents of an email (singular)',
          count: String(count),
        })
      : gt('Show {count} split documents', {
          context:
            'button label to show split-child documents of an email (plural)',
          count: String(count),
        });

  return (
    <div className="pt-2 mt-2 border-t">
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs"
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
      >
        {buttonLabel}
      </Button>
      {show &&
        (attachments?.length ? (
          <div className="mt-2 space-y-2 rounded-md border border-dashed p-2">
            <h6 className="text-xs font-medium text-muted-foreground">
              <T context="heading for split-child documents of an email">
                Split documents
              </T>
            </h6>
            {attachments.map((attachment) =>
              renderAttachmentItem(attachment, false, onRefreshData),
            )}
          </div>
        ) : isLoading ? (
          <p className="text-xs text-muted-foreground italic p-2">
            <T context="email viewer, loading split documents">
              Loading split documents...
            </T>
          </p>
        ) : (
          <p className="text-xs text-muted-foreground italic p-2">
            <T context="email viewer, no split documents found">
              No split documents found
            </T>
          </p>
        ))}
    </div>
  );
}

export function renderEmailAttachments(
  attachments?: EmailAttachment[],
  documentAssociations?: DocumentAssociation[],
  highlightedDocumentIds: string[] = [],
  onRefreshData?: () => void,
): ReactNode {
  const allAttachments: NormalizedEmailAttachment[] = [
    ...(attachments || []).map((att) => ({
      id: att.id,
      documentId: undefined, // Legacy attachments don't have document IDs
      fileName: att.fileName,
      sizeBytes: att.sizeBytes,
      mimeType: att.mimeType,
      s3Url: att.s3Url,
    })),
    ...documentAssociationsToAttachmentItems(documentAssociations),
  ];

  if (!allAttachments.length) return null;

  // Sort PDFs first
  const sortedAttachments = allAttachments.sort((a, b) => {
    const aIsPdf = isPdf(a.fileName, a.mimeType);
    const bIsPdf = isPdf(b.fileName, b.mimeType);
    if (aIsPdf && !bIsPdf) return -1;
    if (!aIsPdf && bIsPdf) return 1;
    return 0;
  });

  return (
    <div className="space-y-4">
      {sortedAttachments.map((attachment) =>
        renderAttachmentItem(
          attachment,
          Boolean(
            attachment.documentId &&
            highlightedDocumentIds.includes(attachment.documentId),
          ),
          onRefreshData,
        ),
      )}
    </div>
  );
}
